import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { productsServices } from "../../services/productsServices.js";
import Parcelamento from "./parcelamento.jsx";
import ModalPayments from "../../components/Checkout/ModalPayments.jsx";


const CheckAnimation = ({ isVisible }) => {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
      transition={{ type: "spring", damping: 10, stiffness: 100 }}
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
    >
      <div className="bg-green-600 rounded-full p-6 shadow-lg">
        <svg
          className="w-16 h-16 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <motion.path
            initial={{ pathLength: 0 }}
            animate={isVisible ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
    </motion.div>
  );
};

// Função utilitária para normalizar URLs
function joinUrl(base, path) {
  if (!base.endsWith("/") && !path.startsWith("/")) {
    return `${base}/${path}`;
  }
  if (base.endsWith("/") && path.startsWith("/")) {
    return `${base}${path.slice(1)}`;
  }
  return `${base}${path}`;
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState("");
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [zoomBackgroundPosition, setZoomBackgroundPosition] = useState("0% 0%");
  const imageRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [modalIsOpen, setAnimateCheck] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);


  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await productsServices.getProductById(id);

        if (typeof result === "string" || result?.message) {
          throw new Error(result.message || result);
        }

        if (!result) {
          throw new Error("Produto não encontrado");
        }

        setProduct(result);

        if (result.images?.length > 0) {
          const mainImageIndex = result.images.findIndex((img) => img.is_main);
          setSelectedImage(mainImageIndex >= 0 ? mainImageIndex : 0);
        }

        if (result.colors?.length > 0) {
          setSelectedColor(result.colors[0].id || result.colors[0]);
        }
      } catch (err) {
        setError(err.message || "Erro ao carregar o produto");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const increaseQuantity = () => {
    if (!product?.stock) return;

    const maxQuantity = getCurrentStock();
    if (quantity < maxQuantity) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  

  const getCurrentStock = () => {
    if (!product?.stock) return 0;

    return typeof product.stock === "number"
      ? product.stock
      : selectedColor
      ? product.stock[selectedColor] || 0
      : 0;
  };

  const stockStatus = () => {
    const stock = getCurrentStock();

    if (stock <= 0) return { text: "Indisponível", class: "text-red-600" };
    if (stock > 10) return { text: "Em estoque", class: "text-green-600" };
    if (stock > 3) return { text: "Estoque limitado", class: "text-amber-600" };
    return { text: "Últimas unidades", class: "text-red-600" };
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;

    const { left, top, width, height } =
      imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setZoomPosition({ x: e.clientX - left - 52, y: e.clientY - top - 52 });
    setZoomBackgroundPosition(`${x}% ${y}%`);
  };

  const HandleAddProductCart = (product) => {
    const productToAdd = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      image: `${process.env.REACT_APP_API_URL}/${product.main_image_url}`,
      color: selectedColor,
    };

    const cartAtual = JSON.parse(localStorage.getItem("cartCache")) || [];
    const novoCarrinho = [...cartAtual, productToAdd];
    localStorage.setItem("cartCache", JSON.stringify(novoCarrinho));

    window.dispatchEvent(new Event("cartUpdated"));

    setAnimateCheck(true);

    setTimeout(() => {
      setAnimateCheck(false);
    }, 2000);
  };

  const HandleBuyProduct = (product) => {
    const productToAdd = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      image: `${process.env.REACT_APP_API_URL}/${product.main_image_url}`,
      color: selectedColor,
    };

    const cartAtual = JSON.parse(localStorage.getItem("cartCache")) || [];
    const novoCarrinho = [...cartAtual, productToAdd];
    localStorage.setItem("cartCache", JSON.stringify(novoCarrinho));
    navigate("/cart");
  };

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 gap-6 py-6 my-10 mx-[3%] mt-0 md:mt-16">
        <div className="flex flex-col gap-4">
          <div className="w-full max-w-[80vh] h-[55vh] bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="grid grid-cols-4 gap-2 w-1/2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-20 bg-gray-200 rounded-lg animate-pulse"
              ></div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="h-10 bg-gray-200 rounded w-3/4 animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2 animate-pulse"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-4 bg-gray-200 rounded w-full animate-pulse"
              ></div>
            ))}
          </div>
          <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>
          <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <p className="text-center py-10">Nenhum dado de produto encontrado</p>
    );
  }

  const status = stockStatus();
  const currentStock = getCurrentStock();

  const productImages =
    product.images?.length > 0
      ? product.images.map((img) =>
          joinUrl(process.env.REACT_APP_API_URL, img.image_url)
        )
      : [joinUrl(process.env.REACT_APP_API_URL, product.main_image_url)];


  const price = Number(product.price || 0);
  const discountPercentage = Number(product.discount_percentage || 0);
  const discountPrice =
    discountPercentage > 0 ? price * (1 - discountPercentage / 100) : null;

  return (
    <div className="grid md:grid-cols-2 gap-6 py-6 my-10 mx-[3%]">
      {/* Product Images */}
      <div className="flex flex-col gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-lg w-full max-w-[80vh] h-[55vh]"
          onMouseEnter={() => setShowZoom(true)}
          onMouseLeave={() => setShowZoom(false)}
          onMouseMove={handleMouseMove}
          ref={imageRef}
        >
          <img
            src={productImages[selectedImage]}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-contain"
            onError={(e) => {
              e.target.src = "";
            }}
          />

          {showZoom && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute w-[120px] h-[120px] rounded-full border-2 border-white bg-white shadow-lg pointer-events-none z-10"
              style={{
                left: `${zoomPosition.x}px`,
                top: `${zoomPosition.y}px`,
                backgroundImage: `url(${productImages[selectedImage]})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: zoomBackgroundPosition,
                backgroundSize: `${imageRef.current?.offsetWidth * 1.5}px ${
                  imageRef.current?.offsetHeight * 1.5
                }px`,
                transform: "translate(-50%, -50%)",
              }}
            />
          )}
        </motion.div>

        {productImages.length > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="grid grid-cols-4 gap-2 w-1/2"
          >
            {productImages.map((image, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`overflow-hidden rounded-lg border cursor-pointer hover:ring-2 hover:ring-green-600 transition-all ${
                  selectedImage === i ? "ring-2 ring-green-600" : ""
                }`}
                onClick={() => setSelectedImage(i)}
              >
                <div className="p-0">
                  <img
                    src={image}
                    alt={`${product.name} - Visualização ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto aspect-square object-contain"
                    onError={(e) => {
                      e.target.src = "";
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Product Details */}
      <div className="flex flex-col gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold">
            {product.name || "Produto sem nome"}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            {discountPrice > 0 ? (
              <>
                <span className="text-2xl font-bold text-green-600">
                  R$ {discountPrice.toFixed(2)}
                </span>
                <span className="text-lg text-gray-500 line-through">
                  R$ {price.toFixed(2)}
                </span>
                <span className="text-sm bg-red-100 text-red-600 px-2 py-1 rounded">
                  {discountPercentage}% OFF
                </span>
              </>
            ) : (
              <span className="text-2xl font-bold text-green-600">
                R$ {price.toFixed(2)}
              </span>
            )}
          </div>
        </motion.div>

        {product.description && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-gray-500"
          >
            {product.description}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="flex items-center gap-2"
        >
          <span className={`font-medium ${status.class}`}>{status.text}</span>
          {currentStock !== undefined && (
            <span className="text-sm text-gray-500">
              ({currentStock} {currentStock === 1 ? "unidade" : "unidades"}{" "}
              disponível)
            </span>
          )}
        </motion.div>

        <motion.hr
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2 }}
          className="border-gray-200"
        />

        {/* Formas de pagamento*/}
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <div className="flex">
              <motion.div>
                <Parcelamento
                  price={price}
                  discountPrice={discountPrice}
                  discountPercentage={discountPercentage}
                />

                <div className="mt-3 underline">
                  <button  onClick={() => setPaymentModalOpen(true)} className="underline">Ver mais opções de pagamento</button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </>

        {/* Quantity Selection */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <label className="text-base font-medium mb-2 block">Quantidade</label>
          <div className="flex items-center">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              type="button"
              className="p-2 border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={decreaseQuantity}
              disabled={quantity <= 1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </motion.button>
            <span className="w-12 text-center font-medium">{quantity}</span>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              type="button"
              className="p-2 border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={increaseQuantity}
              disabled={quantity >= currentStock}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </motion.button>
          </div>
        </motion.div>

        <motion.hr
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.7, duration: 1.2 }}
          className="border-gray-200"
        />

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-3 mt-2"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            className="flex-1 flex items-center justify-center p-3 border rounded-md bg-white hover:bg-gray-50"
            onClick={() => HandleAddProductCart(product)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2"
            >
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            Adicionar ao carrinho
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => HandleBuyProduct(product)}
            className="flex-1 p-3 rounded-md bg-green-600 hover:bg-green-700
            text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="inline mr-2"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
            Comprar agora
          </motion.button>
        </motion.div>
      </div>

      {/* Animação de check */}
      <CheckAnimation isVisible={modalIsOpen} />

      {paymentModalOpen && (
      <ModalPayments
        price={price}
        discountPrice={discountPrice}
        selectedPayment={selectedPayment}
        setSelectedPayment={setSelectedPayment}
        setPaymentModalOpen={setPaymentModalOpen}
      />
    )}
    </div>
  );
}